import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { RefreshCw, Ban, Shield, ShieldOff, Activity } from 'lucide-react';
import { Section, Toolbar } from '@vnetwork/ui/layout';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Skeleton,
  SegmentedControl,
  Switch,
  IconButton,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Button,
  Badge,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
} from '@vnetwork/ui/primitives';
import { Stat, StatGroup, EmptyState, DataTable, type ColumnDef } from '@vnetwork/ui/data';
import { Chart, PieChart } from '@vnetwork/ui/charts';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { useWafStats, useWafEvents, useSaveFirewallRule } from '@/api/manticore';
import { errorMessage } from '@/api/error-message';
import { trackFeatureAction } from '@/lib/analytics-events';
import { toneColor } from '../../shared';
import { asBars, fmtBucket, BarCard } from '../../_shared/analytics';
import styles from './WafAnalyticsTab.module.css';

/**
 * WAF analytics — Analytics-page archetype, mirroring the legacy vEdge layout:
 * stat header (blocked/detected/monitored/total/unique) → disposition donut +
 * events-over-time → ranked breakdowns (rules, categories, countries, paths,
 * user agents) → a Top-attackers-by-IP table.
 *
 * Binds to the REAL `useWafStats` response (vendor-unwrapped in the connector):
 * `overview.{blocked,detected,monitored,total,unique_attackers}`,
 * `timeline[{action,category,events,hour}]`, and breakdown arrays
 * `top_rules | top_categories | top_paths | top_attackers | top_user_agents`.
 */

const RANGES = [
  { value: '1', label: '1H' },
  { value: '6', label: '6H' },
  { value: '24', label: '24H' },
  { value: '168', label: '7D' },
  { value: '720', label: '30D' },
];

const VIEWS = [
  { value: 'dashboard', label: 'Dashboard' },
  { value: 'events', label: 'Event Log' },
];

const num = (o: Record<string, unknown> | null | undefined, k: string) => Number(o?.[k] ?? 0);

interface Attacker {
  client_ip?: string;
  country_code?: string;
  asn?: number;
  asn_org?: string;
  events?: number;
  blocked?: number;
  categories?: string[];
}

// A one-click firewall rule that blocks a single client IP (field `ip_src eq`),
// matching the Firewall Rules tab's wire format so it shows up there too.
function blockRuleForIp(ip: string) {
  return {
    name: `Block ${ip}`,
    description: 'Added from WAF analytics',
    priority: 100,
    enabled: true,
    action: 'block',
    action_params: { status_code: 403 },
    tags: ['waf-analytics'],
    conditions: [
      { condition_group: 0, field: 'ip_src', field_key: null, operator: 'eq', value: ip, negate: false, transformations: [], case_insensitive: false, sequence: 0 },
    ],
  };
}

export function WafAnalyticsTab({ domain, domainId }: { domain: string; domainId: string }) {
  const { show } = useToast();
  const [hours, setHours] = useState('24');
  const [view, setView] = useState('dashboard');
  const [blockTarget, setBlockTarget] = useState<Attacker | null>(null);
  const { data, isLoading } = useWafStats(domainId, `hours=${hours}`);

  const blockRule = useSaveFirewallRule(domainId, {
    onSuccess: () => show({ tone: 'success', title: 'IP blocked', description: 'A firewall rule was created in Firewall Rules.' }),
    onError: (e) => show({ tone: 'danger', title: 'Could not block IP', description: errorMessage(e, 'Could not block IP') }),
  });
  const overview = (data?.overview as Record<string, unknown>) ?? data ?? null;

  const blocked = num(overview, 'blocked');
  const detected = num(overview, 'detected');
  const monitored = num(overview, 'monitored');
  const total = num(overview, 'total');
  const allowed = Math.max(0, total - blocked - detected);

  const disposition = useMemo(
    () =>
      [
        { name: 'Blocked', value: blocked, color: toneColor('danger') },
        { name: 'Detected', value: detected, color: toneColor('warning') },
        { name: 'Allowed', value: allowed, color: 'var(--vn-success-solid)' },
      ].filter((s) => s.value > 0),
    [blocked, detected, allowed],
  );

  // BE timeline rows are `{ action, category, events, hour }` (one per
  // action+category per hour) — fold them into per-hour blocked/detected points.
  const timeline = useMemo(() => {
    const raw = data?.timeline;
    const rows = Array.isArray(raw) ? (raw as Record<string, unknown>[]) : [];
    const byHour = new Map<string, { t: string; blocked: number; detected: number }>();
    for (const r of rows) {
      const hour = String(r.hour ?? r.time ?? r.timestamp ?? '');
      const pt = byHour.get(hour) ?? { t: fmtBucket(hour), blocked: 0, detected: 0 };
      const n = Number(r.events ?? r.count ?? 0);
      if (r.action === 'block') pt.blocked += n;
      else pt.detected += n;
      byHour.set(hour, pt);
    }
    return [...byHour.values()];
  }, [data]);
  const series = useMemo(
    () => [
      { key: 'blocked', label: 'Blocked', color: toneColor('danger') },
      { key: 'detected', label: 'Detected', color: toneColor('warning') },
    ],
    [],
  );

  const attackers = useMemo(
    () => (Array.isArray(data?.top_attackers) ? (data!.top_attackers as Attacker[]) : []),
    [data],
  );

  // No dedicated countries breakdown — aggregate the attacker list by country.
  const countries = useMemo(() => {
    const m = new Map<string, number>();
    for (const a of attackers) {
      const c = String(a.country_code ?? '—');
      m.set(c, (m.get(c) ?? 0) + Number(a.events ?? a.blocked ?? 0));
    }
    return [...m.entries()]
      .map(([label, value]) => ({ label, value, display: value.toLocaleString() }))
      .filter((b) => b.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [attackers]);

  const attackerCols = useMemo<ColumnDef<Attacker, unknown>[]>(
    () => [
      {
        id: 'ip',
        header: 'IP address',
        cell: ({ row }) => (
          <span style={{ fontFamily: 'var(--vn-font-mono, ui-monospace, monospace)', fontSize: 'var(--vn-font-size-md)' }}>{row.original.client_ip ?? '—'}</span>
        ),
      },
      { id: 'country', header: 'Country', cell: ({ row }) => row.original.country_code ?? '—' },
      { id: 'asn', header: 'ASN', cell: ({ row }) => (row.original.asn != null ? String(row.original.asn) : '—') },
      {
        id: 'org',
        header: 'ISP / Organization',
        cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>{row.original.asn_org ?? '—'}</span>,
      },
      { id: 'events', header: 'Events', cell: ({ row }) => (row.original.events ?? 0).toLocaleString() },
      {
        id: 'blocked',
        header: 'Blocked',
        cell: ({ row }) => <span style={{ color: toneColor('danger', 'text') }}>{(row.original.blocked ?? 0).toLocaleString()}</span>,
      },
      {
        id: 'categories',
        header: 'Categories',
        cell: ({ row }) => (
          <div style={{ display: 'flex', gap: 'var(--vn-space-1)', flexWrap: 'wrap' }}>
            {(row.original.categories ?? []).map((c) => (
              <Badge key={c} tone="neutral" appearance="subtle" size="sm">
                {c}
              </Badge>
            ))}
          </div>
        ),
      },
      {
        id: 'action',
        header: '',
        cell: ({ row }) =>
          row.original.client_ip ? (
            <Button
              variant="ghost"
              size="sm"
              startIcon={<Ban size={14} />}
              style={{ color: toneColor('danger', 'text') }}
              onClick={() => setBlockTarget(row.original)}
            >
              Block
            </Button>
          ) : null,
      },
    ],
    [],
  );

  return (
    <Section
      title="WAF Analytics"
      description={`Blocked and detected requests for ${domain}.`}
      actions={
        <div style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center' }}>
          <SegmentedControl size="sm" items={VIEWS} value={view} onValueChange={setView} aria-label="View" />
          <SegmentedControl size="sm" items={RANGES} value={hours} onValueChange={setHours} aria-label="Time range" />
        </div>
      }
    >
      {view === 'events' ? (
        <WafEventLog domainId={domainId} hours={hours} />
      ) : isLoading ? (
        <Skeleton style={{ height: 240 }} />
      ) : !data ? (
        <EmptyState icon={<ShieldOff size={20} />} title="No WAF analytics yet" description="No data for the selected range." />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          {/* Stat header (5 KPIs, matching legacy) */}
          <Card flat>
            <CardBody>
              <StatGroup columns={5}>
                <Stat label="Blocked" value={blocked.toLocaleString()} dotColor={toneColor('danger')} />
                <Stat label="Detected" value={detected.toLocaleString()} dotColor={toneColor('warning')} />
                <Stat label="Monitored" value={monitored.toLocaleString()} dotColor={toneColor('info')} />
                <Stat label="Total events" value={total.toLocaleString()} />
                <Stat label="Unique attackers" value={num(overview, 'unique_attackers').toLocaleString()} />
              </StatGroup>
            </CardBody>
          </Card>

          {/* Disposition donut + events over time */}
          <div className={styles.dispositionGrid}>
            <Card>
              <CardHeader>
                <CardTitle>Request disposition</CardTitle>
              </CardHeader>
              <CardBody>
                {disposition.length === 0 ? (
                  <EmptyState size="sm" icon={<Activity size={20} />} title="No traffic" description="No traffic in the selected range." />
                ) : (
                  <PieChart
                    donut
                    height={180}
                    data={disposition}
                    showLegend
                    centerLabel={
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 'var(--vn-font-size-xl)', fontWeight: 'var(--vn-font-weight-semibold)' }}>{(blocked + detected).toLocaleString()}</div>
                        <div style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>actioned</div>
                      </div>
                    }
                  />
                )}
              </CardBody>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>WAF events over time</CardTitle>
              </CardHeader>
              <CardBody>
                {timeline.length === 0 ? (
                  <EmptyState size="sm" icon={<Activity size={20} />} title="No events" description="No events in the selected range." />
                ) : (
                  <Chart type="area" data={timeline} xKey="t" series={series} height={200} stacked showGrid />
                )}
              </CardBody>
            </Card>
          </div>

          {/* Ranked breakdowns */}
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
            <BarCard title="Top triggered rules" items={asBars(data.top_rules, { labelKeys: ['rule_name', 'rule_id'] })} />
            <BarCard title="Attack categories" items={asBars(data.top_categories, { labelKeys: ['category'] })} />
            <BarCard title="Top countries" items={countries} />
            <BarCard title="Most targeted paths" items={asBars(data.top_paths, { labelKeys: ['path'] })} />
            <BarCard title="Top user agents" items={asBars(data.top_user_agents, { labelKeys: ['user_agent'] })} />
          </div>

          {/* Top attackers by IP */}
          {attackers.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Top attackers by IP</CardTitle>
              </CardHeader>
              <CardBody>
                <DataTable
                  columns={attackerCols}
                  data={attackers}
                  getRowId={(r) => r.client_ip ?? ''}
                  emptyState={
                    <EmptyState
                      size="sm"
                      icon={<Shield size={28} />}
                      title="No attackers in range"
                      description="No attacker IPs were recorded for the selected range."
                    />
                  }
                />
              </CardBody>
            </Card>
          )}
        </div>
      )}

      <ConfirmDialog
        open={!!blockTarget}
        onOpenChange={(o) => !o && setBlockTarget(null)}
        title="Block this IP?"
        description={
          blockTarget
            ? `Create a firewall rule that blocks ${blockTarget.client_ip} (${blockTarget.country_code ?? '—'}). You can edit or remove it later in Firewall Rules.`
            : ''
        }
        confirmLabel="Block IP"
        destructive
        onConfirm={() => {
          if (blockTarget?.client_ip) blockRule.mutate(blockRuleForIp(blockTarget.client_ip), { onSuccess: () => trackFeatureAction('cdn', 'firewall_rule', 'create') });
          setBlockTarget(null);
        }}
      />
    </Section>
  );
}

// ── Event Log view ────────────────────────────────────────────────────────
interface WafEvent {
  event_time?: string;
  action?: string;
  block_reason?: string;
  rule_id?: string;
  rule_name?: string;
  category?: string;
  severity?: string;
  waf_source?: string;
  anomaly_score?: number;
  threat_score?: number;
  matched_field?: string;
  matched_value?: string;
  client_ip?: string;
  country_code?: string;
  asn?: number;
  asn_org?: string;
  method?: string;
  path?: string;
  query?: string;
  protocol?: string;
  referer?: string;
  user_agent?: string;
  request_id?: string;
}

const sevTone = (s?: string): 'danger' | 'warning' | 'info' | 'neutral' => {
  const v = (s ?? '').toLowerCase();
  return v === 'critical' || v === 'high' ? 'danger' : v === 'medium' ? 'warning' : v === 'low' ? 'info' : 'neutral';
};
const actionTone = (a?: string): 'danger' | 'warning' | 'success' | 'neutral' => {
  const v = (a ?? '').toLowerCase();
  return v === 'block' ? 'danger' : v === 'detect' || v === 'log' || v === 'challenge' ? 'warning' : v === 'allow' ? 'success' : 'neutral';
};
const fmtEventTime = (t?: string) => (t ? t.replace('T', ' ') : '—');
const mono: CSSProperties = { fontFamily: 'var(--vn-font-mono, ui-monospace, monospace)', fontSize: 'var(--vn-font-size-sm)' };

const EV_CATEGORIES = ['all', 'sqli', 'xss', 'rce', 'lfi', 'ssrf', 'scanner', 'protocol', 'xxe', 'deserialization', 'log4shell', 'threat', 'bot', 'custom', 'graphql'];
const EV_ACTIONS = ['all', 'block', 'detect', 'monitor', 'log'];

function WafEventLog({ domainId, hours }: { domainId: string; hours: string }) {
  const [cat, setCat] = useState('all');
  const [act, setAct] = useState('all');
  const [auto, setAuto] = useState(false);
  const [selected, setSelected] = useState<WafEvent | null>(null);
  const query =
    `hours=${hours}` + (cat !== 'all' ? `&category=${cat}` : '') + (act !== 'all' ? `&action=${act}` : '') + '&limit=100';
  const { data, isLoading, refetch, isFetching } = useWafEvents(domainId, query);

  useEffect(() => {
    if (!auto) return;
    const t = setInterval(() => refetch(), 30000);
    return () => clearInterval(t);
  }, [auto, refetch]);

  const events = useMemo(() => {
    const d = data as { events?: WafEvent[]; total?: number } | null;
    return Array.isArray(d?.events) ? (d!.events as WafEvent[]) : [];
  }, [data]);
  const total = (data as { total?: number } | null)?.total ?? events.length;

  const columns = useMemo<ColumnDef<WafEvent, unknown>[]>(
    () => [
      { id: 'time', header: 'Time', cell: ({ row }) => <span style={{ ...mono, color: 'var(--vn-fg-muted)', whiteSpace: 'nowrap' }}>{fmtEventTime(row.original.event_time)}</span> },
      {
        id: 'action',
        header: 'Action',
        cell: ({ row }) => (
          <div>
            <Badge tone={actionTone(row.original.action)} appearance="subtle" size="sm">{row.original.action ?? '—'}</Badge>
            {row.original.block_reason ? <div style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)', marginTop: 'var(--vn-space-1)' }}>{row.original.block_reason}</div> : null}
          </div>
        ),
      },
      {
        id: 'rule',
        header: 'Rule',
        cell: ({ row }) => (
          <div>
            <div style={{ fontSize: 'var(--vn-font-size-md)' }}>{row.original.rule_name || row.original.rule_id || '—'}</div>
            <div style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
              {[row.original.category, row.original.waf_source].filter(Boolean).join(' · ')}
            </div>
          </div>
        ),
      },
      {
        id: 'severity',
        header: 'Severity',
        cell: ({ row }) => (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
            <Badge tone={sevTone(row.original.severity)} appearance="subtle" size="sm">{row.original.severity ?? '—'}</Badge>
            {row.original.anomaly_score || row.original.threat_score ? (
              <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>AS {row.original.anomaly_score ?? 0} · TS {row.original.threat_score ?? 0}</span>
            ) : null}
          </div>
        ),
      },
      {
        id: 'client',
        header: 'Client',
        cell: ({ row }) => (
          <div>
            <span style={mono}>
              {row.original.client_ip ?? '—'}
              {row.original.country_code ? <span style={{ color: 'var(--vn-fg-muted)' }}> · {row.original.country_code}</span> : null}
            </span>
            {row.original.asn_org ? (
              <div style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.original.asn_org}</div>
            ) : null}
          </div>
        ),
      },
      {
        id: 'req',
        header: 'Request',
        cell: ({ row }) => (
          <span style={{ fontSize: 'var(--vn-font-size-sm)' }}>
            <Badge tone="neutral" appearance="outline" size="sm">{row.original.method ?? '—'}</Badge>{' '}
            <span style={{ ...mono, color: 'var(--vn-fg-muted)' }}>{(row.original.path ?? '').slice(0, 40)}</span>
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <>
      <Card>
        <CardHeader
          actions={
            <div style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center' }}>
              <label style={{ display: 'flex', gap: 'var(--vn-space-1)', alignItems: 'center', fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
                <Switch size="sm" checked={auto} onCheckedChange={setAuto} /> Auto-refresh
              </label>
              <IconButton variant="ghost" size="sm" aria-label="Refresh" disabled={isFetching} onClick={() => refetch()}>
                <RefreshCw size={15} />
              </IconButton>
            </div>
          }
        >
          <CardTitle>Event log</CardTitle>
          <CardDescription>Showing {events.length.toLocaleString()} of {total.toLocaleString()} events · click a row for details.</CardDescription>
        </CardHeader>
        <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <Toolbar
            end={
              <>
                <Select value={cat} onValueChange={setCat}>
                  <SelectTrigger style={{ width: 160 }}><SelectValue /></SelectTrigger>
                  <SelectContent>{EV_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c === 'all' ? 'All categories' : c}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={act} onValueChange={setAct}>
                  <SelectTrigger style={{ width: 140 }}><SelectValue /></SelectTrigger>
                  <SelectContent>{EV_ACTIONS.map((a) => <SelectItem key={a} value={a}>{a === 'all' ? 'All actions' : a}</SelectItem>)}</SelectContent>
                </Select>
              </>
            }
          />
          {isLoading ? (
            <Skeleton style={{ height: 200 }} />
          ) : (
            <DataTable
              columns={columns}
              data={events}
              getRowId={(r) => r.request_id ?? `${r.event_time ?? ''}-${r.client_ip ?? ''}-${r.rule_id ?? ''}`}
              onRowClick={(r) => setSelected(r)}
              emptyState={
                <EmptyState
                  size="sm"
                  icon={<Shield size={28} />}
                  title="No WAF events"
                  description="No events match the selected filters."
                />
              }
            />
          )}
        </CardBody>
      </Card>
      {selected && <WafEventDetail event={selected} onClose={() => setSelected(null)} />}
    </>
  );
}

// ── Event detail dialog ───────────────────────────────────────────────────
function DRow({ label, value, code }: { label: string; value?: ReactNode; code?: boolean }) {
  const empty = value === undefined || value === null || value === '';
  return (
    <div className={styles.detailRow}>
      <span style={{ color: 'var(--vn-fg-muted)' }}>{label}</span>
      <span style={{ ...(code ? mono : {}), wordBreak: 'break-word' }}>{empty ? '—' : value}</span>
    </div>
  );
}

function DGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 'var(--vn-font-size-xs)', fontWeight: 'var(--vn-font-weight-semibold)', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--vn-fg-muted)', marginBottom: 'var(--vn-space-1)' }}>{title}</div>
      <div>{children}</div>
    </div>
  );
}

function WafEventDetail({ event, onClose }: { event: WafEvent; onClose: () => void }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent style={{ maxWidth: 720 }}>
        <DialogHeader>
          <DialogTitle>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
              <Badge tone={actionTone(event.action)} appearance="subtle" size="sm">{event.action ?? '—'}</Badge>
              {event.rule_name || event.rule_id || 'WAF event'}
            </span>
          </DialogTitle>
          <DialogDescription>{[fmtEventTime(event.event_time), event.request_id].filter(Boolean).join('  ·  ')}</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            <DGroup title="Request">
              <DRow label="Method" value={event.method} />
              <DRow label="Path" value={event.path} code />
              <DRow label="Query" value={event.query} code />
              <DRow label="Protocol" value={event.protocol} />
              <DRow label="Referer" value={event.referer} code />
              <DRow label="User agent" value={event.user_agent} code />
            </DGroup>
            <DGroup title="Client">
              <DRow label="IP address" value={event.client_ip} code />
              <DRow label="Country" value={event.country_code} />
              <DRow label="ASN" value={event.asn != null ? String(event.asn) : undefined} />
              <DRow label="Organization" value={event.asn_org} />
            </DGroup>
            <DGroup title="Detection">
              <DRow label="Category" value={event.category} />
              <DRow label="Severity" value={<Badge tone={sevTone(event.severity)} appearance="subtle" size="sm">{event.severity ?? '—'}</Badge>} />
              <DRow label="Source" value={event.waf_source} />
              <DRow label="Block reason" value={event.block_reason} code />
              <DRow label="Matched" value={event.matched_field ? `${event.matched_field} = ${event.matched_value ?? ''}` : undefined} code />
              <DRow label="Anomaly score" value={String(event.anomaly_score ?? 0)} />
              <DRow label="Threat score" value={String(event.threat_score ?? 0)} />
              <DRow label="Rule ID" value={event.rule_id} code />
            </DGroup>
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
